"use client"

import {
  getListPagination,
  ListPagination,
  readListPage,
  writeListPage,
  PaneListSkeleton,
  PaneResponsiveCardGrid,
  PaneListEmptyState,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
} from '@yeshuman/sdk'
import { useSearchParams } from 'react-router-dom'
import { useMyApplications } from '../../hooks/use-my-applications'
import { ApplicationListCardRow } from './application-list-card-row'
import { FileText, Search } from 'lucide-react'

type MyApplicationsProps = {
  /** When set, empty state offers a CTA into Search (Jobs pipeline). */
  onBrowseJobs?: () => void
}

export function MyApplications({ onBrowseJobs }: MyApplicationsProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const listPagination = getListPagination('candidateApplied')
  const page = readListPage(searchParams)
  const { applications, pagination, isLoading, error } = useMyApplications({
    page: listPagination.enabled ? page : undefined,
    pageSize: listPagination.pageSize,
  })

  if (isLoading) {
    return <PaneListSkeleton count={4} ariaLabel="Loading your applications" />
  }

  if (error) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-destructive">Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    )
  }

  const paginationFooter =
    listPagination.enabled && pagination ? (
      <ListPagination
        page={pagination.page}
        pageSize={pagination.page_size}
        total={pagination.total}
        onPageChange={(nextPage) => {
          setSearchParams(writeListPage(searchParams, nextPage), { replace: true })
        }}
      />
    ) : null

  if (applications.length === 0) {
    const hasApplicationsElsewhere = (pagination?.total ?? 0) > 0
    return (
      <div className="space-y-4">
        <Card className="w-full">
          <CardContent>
            <PaneListEmptyState
              icon={<FileText aria-hidden />}
              title={hasApplicationsElsewhere ? 'No applications on this page' : 'No applications yet'}
              description={
                hasApplicationsElsewhere
                  ? 'Try a previous page — your applications are still here.'
                  : "You haven't applied to any jobs yet. Search open roles and start applying."
              }
              action={
                !hasApplicationsElsewhere && onBrowseJobs ? (
                  <Button type="button" variant="secondary" size="sm" onClick={onBrowseJobs}>
                    <Search className="mr-2 h-4 w-4" aria-hidden />
                    Browse jobs
                  </Button>
                ) : undefined
              }
            />
          </CardContent>
        </Card>
        {paginationFooter}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PaneResponsiveCardGrid>
        {applications.map((application) => (
          <ApplicationListCardRow key={application.id} application={application} />
        ))}
      </PaneResponsiveCardGrid>
      {paginationFooter}
    </div>
  )
}
